import { useState, useMemo, useEffect } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Calculator, Download, ShieldAlert, Save, Trash2, FolderOpen } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function MarginCalculator() {
  const { user } = useAuth();
  const [f, setF] = useState({ item_name: "", customer_name: "", agent_name: "", buy_cost: "", sell_price: "", margin_pct: "", quantity: 1, commission_pct: 2, commission_override: "", other_expenses: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState("");
  const [monthFilter, setMonthFilter] = useState("");

  const isAdmin = user?.role === "super_admin";
  useEffect(() => {
    if (isAdmin) api.get("/margin-calc").then((r) => setHistory(r.data)).catch(() => {});
  }, [isAdmin]);

  const num = (v) => Number(v) || 0;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // Editing margin % recalculates sell price; editing sell price recalculates margin %
  const setMargin = (e) => {
    const m = e.target.value;
    const sell = num(f.buy_cost) ? (num(f.buy_cost) * (1 + num(m) / 100)).toFixed(2) : f.sell_price;
    setF({ ...f, margin_pct: m, sell_price: sell });
  };
  const setSell = (e) => {
    const s = e.target.value;
    const m = num(f.buy_cost) ? (((num(s) - num(f.buy_cost)) / num(f.buy_cost)) * 100).toFixed(2) : f.margin_pct;
    setF({ ...f, sell_price: s, margin_pct: m });
  };

  const calc = useMemo(() => {
    const qty = Math.max(1, num(f.quantity));
    const commissionPerUnit = f.commission_override !== "" ? num(f.commission_override) : (num(f.sell_price) * num(f.commission_pct)) / 100;
    const totalCost = num(f.buy_cost) * qty + num(f.other_expenses);
    const totalCommission = commissionPerUnit * qty;
    const totalRevenue = num(f.sell_price) * qty;
    const netProfit = totalRevenue - totalCost - totalCommission;
    const netMargin = totalRevenue ? (netProfit / totalRevenue) * 100 : 0;
    return { qty, commissionPerUnit, totalCost, totalCommission, totalRevenue, netProfit, netMargin };
  }, [f]);

  const payload = () => ({
    item_name: f.item_name || null,
    customer_name: f.customer_name || null,
    agent_name: f.agent_name || null,
    buy_cost: num(f.buy_cost), sell_price: num(f.sell_price), margin_pct: num(f.margin_pct),
    quantity: calc.qty, commission_pct: num(f.commission_pct),
    commission_per_unit: calc.commissionPerUnit, other_expenses: num(f.other_expenses),
  });

  const saveCalc = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post("/margin-calc", payload());
      setHistory((h) => [r.data, ...h]);
      setMsg(`Saved${f.item_name ? `: ${f.item_name}` : ""} — profit ${inr(r.data.net_profit)}`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const loadCalc = (h) => {
    setF({
      item_name: h.item_name || "", customer_name: h.customer_name || "", agent_name: h.agent_name || "",
      buy_cost: String(h.buy_cost), sell_price: String(h.sell_price),
      margin_pct: String(h.margin_pct), quantity: h.quantity, commission_pct: h.commission_pct,
      commission_override: "", other_expenses: String(h.other_expenses || ""),
    });
    setMsg(`Loaded "${h.item_name || "unnamed deal"}" into the calculator`);
  };

  const delCalc = async (h) => {
    if (!window.confirm(`Delete saved calculation${h.item_name ? ` "${h.item_name}"` : ""}?`)) return;
    try { await api.delete(`/margin-calc/${h.id}`); setHistory((x) => x.filter((y) => y.id !== h.id)); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const exportXlsx = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/margin-calc/export", payload(), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "sales-margin-report.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const outputs = [
    { k: "cost", label: "Total Cost (excl. GST)", v: inr(calc.totalCost) },
    { k: "commission", label: "Total Commission", v: inr(calc.totalCommission) },
    { k: "revenue", label: "Total Revenue", v: inr(calc.totalRevenue) },
    { k: "profit", label: "Total Net Profit", v: inr(calc.netProfit), highlight: true, danger: calc.netProfit < 0 },
    { k: "margin", label: "Net Margin %", v: `${calc.netMargin.toFixed(2)}%`, highlight: true, danger: calc.netMargin < 0 },
  ];

  if (user?.role !== "super_admin") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h1 data-testid="margin-access-denied" className="mt-4 font-heading text-2xl font-bold">Access Denied — Admin Only</h1>
        <p className="mt-2 text-sm text-muted-foreground">The Sales Margin Calculator is restricted to the Super Admin.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Super Admin Only</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Sales Margin Calculator</h1>
          <p className="mt-1 text-sm text-muted-foreground">Run buy/sell numbers, commissions and expenses — then export a clean Excel report.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="margin-save" onClick={saveCalc} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-4 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
            <Save className="h-4 w-4" /> Save calculation
          </button>
          <button data-testid="margin-export" onClick={exportXlsx} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
            <Download className="h-4 w-4" /> {busy ? "Working…" : "Export to Excel"}
          </button>
        </div>
      </header>

      {msg && <div data-testid="margin-msg" className="mb-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600">{msg}</div>}

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="sharp-card grid gap-3 p-5 sm:grid-cols-2 self-start">
          <h2 className="sm:col-span-2 flex items-center gap-2 font-heading text-lg font-semibold"><Calculator className="h-5 w-5 text-primary" /> Inputs</h2>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Item / deal name (optional)</label>
            <input data-testid="margin-item" value={f.item_name} onChange={set("item_name")} placeholder="e.g. 24-port switch deal" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Customer name</label>
            <input data-testid="margin-customer" value={f.customer_name} onChange={set("customer_name")} placeholder="e.g. Metro Systems" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Agent name</label>
            <input data-testid="margin-agent" value={f.agent_name} onChange={set("agent_name")} placeholder="e.g. Suresh" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Buy cost / unit (excl. GST)</label>
            <input data-testid="margin-buy" type="number" min="0" step="0.01" value={f.buy_cost} onChange={set("buy_cost")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Sell price / unit (excl. GST)</label>
            <input data-testid="margin-sell" type="number" min="0" step="0.01" value={f.sell_price} onChange={setSell} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Margin % (edits sell price)</label>
            <input data-testid="margin-pct" type="number" step="0.01" value={f.margin_pct} onChange={setMargin} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Quantity</label>
            <input data-testid="margin-qty" type="number" min="1" value={f.quantity} onChange={set("quantity")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Commission % of sell price</label>
            <input data-testid="margin-comm-pct" type="number" min="0" step="0.01" value={f.commission_pct} onChange={set("commission_pct")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            <p className="mt-1 text-[10px] text-muted-foreground">Auto: {inr(calc.commissionPerUnit)} per unit</p>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Commission override / unit (₹)</label>
            <input data-testid="margin-comm-override" type="number" min="0" step="0.01" value={f.commission_override} onChange={set("commission_override")} placeholder="leave blank for auto" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Other expenses (total ₹ — shipping, ops, etc.)</label>
            <input data-testid="margin-other" type="number" min="0" step="0.01" value={f.other_expenses} onChange={set("other_expenses")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </section>

        <section className="sharp-card self-start p-5">
          <h2 className="font-heading text-lg font-semibold">Financial breakdown</h2>
          <div className="mt-4 space-y-2">
            {outputs.map((o) => (
              <div key={o.k} data-testid={`margin-out-${o.k}`} className={`flex items-center justify-between rounded-md border px-4 py-3 ${o.highlight ? (o.danger ? "border-rose-500/50 bg-rose-500/5" : "border-emerald-500/50 bg-emerald-500/5") : "border-border/60"}`}>
                <span className="text-sm">{o.label}</span>
                <span className={`font-heading text-lg font-bold ${o.highlight ? (o.danger ? "text-rose-600" : "text-emerald-600") : ""}`}>{o.v}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">Net Profit = Revenue − (Buy Cost × Qty + Other Expenses) − Commission × Qty</p>
        </section>
      </div>

      <section className="sharp-card mt-6 p-5">
        <h2 className="font-heading text-lg font-semibold">Agent profit summary</h2>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(history.reduce((a, h) => {
            const k = h.agent_name || "Unassigned";
            a[k] = a[k] || { profit: 0, deals: 0 };
            a[k].profit += Number(h.net_profit) || 0; a[k].deals += 1;
            return a;
          }, {})).sort((x, y) => y[1].profit - x[1].profit).map(([name, v]) => (
            <div key={name} data-testid={`agent-summary-${name}`} className="rounded-md border border-border/60 px-4 py-3">
              <p className="text-sm font-medium">{name}</p>
              <p className={`font-heading text-lg font-bold ${v.profit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{inr(v.profit)}</p>
              <p className="text-[11px] text-muted-foreground">{v.deals} deal{v.deals !== 1 ? "s" : ""}</p>
            </div>
          ))}
          {history.length === 0 && <p className="text-xs text-muted-foreground">No saved deals yet.</p>}
        </div>
      </section>

      <section className="sharp-card mt-6 overflow-hidden">
        <div className="flex items-center justify-between border-b border-border/60 bg-secondary/30 px-5 py-3">
          <h2 className="font-heading text-lg font-semibold">Saved deals ({history.filter((h) => !monthFilter || (h.created_at || "").startsWith(monthFilter)).length})</h2>
          <div className="flex items-center gap-2">
            <select
              data-testid="margin-month-filter"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="rounded-md border border-input bg-background px-2.5 py-1.5 text-xs"
            >
              <option value="">All months</option>
              {[...new Set(history.map((h) => (h.created_at || "").slice(0, 7)).filter(Boolean))].sort().reverse().map((m) => (
                <option key={m} value={m}>{new Date(`${m}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</option>
              ))}
            </select>
            <button
            data-testid="margin-export-all"
            onClick={async () => {
              setErr("");
              try {
                const r = await api.get(`/margin-calc/export-all${monthFilter ? `?month=${monthFilter}` : ""}`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = monthFilter ? `margin-deals-${monthFilter}.xlsx` : "margin-deals-all.xlsx";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (e) { setErr(formatApiError(e)); }
            }}
            disabled={history.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-1.5 text-xs font-semibold hover:bg-secondary disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" /> {monthFilter ? "Export month" : "Export all deals"}
          </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 text-left">
              <tr>
                <th className="px-4 py-2 label-eyebrow">Deal</th>
                <th className="px-4 py-2 label-eyebrow">Customer</th>
                <th className="px-4 py-2 label-eyebrow">Agent</th>
                <th className="px-4 py-2 label-eyebrow">Date</th>
                <th className="px-4 py-2 label-eyebrow text-right">Buy / Sell</th>
                <th className="px-4 py-2 label-eyebrow text-right">Qty</th>
                <th className="px-4 py-2 label-eyebrow text-right">Revenue</th>
                <th className="px-4 py-2 label-eyebrow text-right">Net Profit</th>
                <th className="px-4 py-2 label-eyebrow text-right">Margin %</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {history.filter((h) => !monthFilter || (h.created_at || "").startsWith(monthFilter)).map((h) => (
                <tr key={h.id} data-testid={`margin-hist-${h.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2 font-medium">{h.item_name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{h.customer_name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{h.agent_name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                  <td className="px-4 py-2 text-right text-xs text-muted-foreground">{inr(h.buy_cost)} / {inr(h.sell_price)}</td>
                  <td className="px-4 py-2 text-right">{h.quantity}</td>
                  <td className="px-4 py-2 text-right">{inr(h.total_revenue)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${h.net_profit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{inr(h.net_profit)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${h.net_margin < 0 ? "text-rose-600" : "text-emerald-600"}`}>{Number(h.net_margin).toFixed(2)}%</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`margin-load-${h.id}`} title="Load into calculator" onClick={() => loadCalc(h)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><FolderOpen className="h-3.5 w-3.5" /></button>
                      <button data-testid={`margin-del-${h.id}`} title="Delete" onClick={() => delCalc(h)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-xs text-muted-foreground">No saved deals yet — click &ldquo;Save calculation&rdquo; to keep one.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
